import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Award, TrendingUp, Plus, Minus, Crown } from 'lucide-react';
import { toast } from 'sonner';

const tierBadge = {
  bronze: { label: 'Бронза', color: 'bg-orange-100 text-orange-800', icon: '🥉' },
  silver: { label: 'Серебро', color: 'bg-gray-200 text-gray-800', icon: '🥈' },
  gold: { label: 'Золото', color: 'bg-yellow-100 text-yellow-800', icon: '🥇' },
  vip: { label: 'VIP', color: 'bg-purple-100 text-purple-800', icon: '👑' },
};

export default function LoyaltyPage() {
  const [accounts, setAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ company_id: '', points: 100, description: 'Бонус за заказ' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [acc, comp] = await Promise.all([
      axios.get(`${API}/loyalty/all`),
      axios.get(`${API}/companies`),
    ]);
    setAccounts(acc.data);
    setCompanies(comp.data);
  };

  const handleAdjust = async () => {
    try {
      await axios.post(`${API}/loyalty/adjust`, form);
      toast.success('Баллы начислены');
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  return (
    <div className="space-y-6" data-testid="loyalty-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Программа лояльности</h1>
          <p className="text-gray-500 mt-1">Бонусные баллы для постоянных клиентов</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="adjust-loyalty-btn">
              <Plus className="h-4 w-4 mr-2" /> Начислить/Списать
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Начислить или списать баллы</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Клиент</Label>
                <select
                  value={form.company_id}
                  onChange={(e) => setForm({...form, company_id: e.target.value})}
                  className="w-full mt-1 border border-gray-300 rounded-md h-10 px-3"
                  data-testid="loyalty-company-select"
                >
                  <option value="">Выберите...</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div>
                <Label>Баллы (отрицательное = списание)</Label>
                <Input
                  type="number"
                  value={form.points}
                  onChange={(e) => setForm({...form, points: parseInt(e.target.value) || 0})}
                  data-testid="loyalty-points-input"
                />
              </div>
              <div>
                <Label>Причина</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  data-testid="loyalty-desc-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Отмена</Button>
              <Button onClick={handleAdjust} className="cff-button-primary" data-testid="save-loyalty-btn">Сохранить</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tier info */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Уровни</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center p-3 bg-orange-50 rounded-md">
            <p className="text-3xl">🥉</p>
            <p className="font-semibold text-sm mt-1">Бронза</p>
            <p className="text-xs text-gray-500">от 0</p>
          </div>
          <div className="text-center p-3 bg-gray-100 rounded-md">
            <p className="text-3xl">🥈</p>
            <p className="font-semibold text-sm mt-1">Серебро</p>
            <p className="text-xs text-gray-500">от 1,000</p>
          </div>
          <div className="text-center p-3 bg-yellow-50 rounded-md">
            <p className="text-3xl">🥇</p>
            <p className="font-semibold text-sm mt-1">Золото</p>
            <p className="text-xs text-gray-500">от 5,000</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-md">
            <p className="text-3xl">👑</p>
            <p className="font-semibold text-sm mt-1">VIP</p>
            <p className="text-xs text-gray-500">от 10,000</p>
          </div>
        </div>
      </Card>

      {/* Accounts */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Клиенты с баллами</h3>
        {accounts.length === 0 ? (
          <p className="text-center py-12 text-gray-400 text-sm">Пока никто не накопил баллов</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="pb-3 font-medium">Клиент</th>
                  <th className="pb-3 font-medium">Уровень</th>
                  <th className="pb-3 font-medium text-right">Баланс</th>
                  <th className="pb-3 font-medium text-right">Всего заработано</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(a => {
                  const tier = tierBadge[a.tier] || tierBadge.bronze;
                  return (
                    <tr key={a.id} className="border-b border-gray-100" data-testid={`loyalty-row-${a.client_code}`}>
                      <td className="py-3">
                        <p className="font-medium">{a.company_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{a.client_code}</p>
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${tier.color}`}>
                          {tier.icon} {tier.label}
                        </span>
                      </td>
                      <td className="py-3 text-right font-bold cff-text-numeric text-[#556B2F]">
                        {a.points_balance.toLocaleString()}
                      </td>
                      <td className="py-3 text-right text-gray-500 cff-text-numeric">
                        {a.lifetime_points.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
